import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { User } from 'models/user'
import { Card } from 'components/Card'
import { Flex } from 'components/Flex'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Button } from 'components/Button'
import { Alert } from 'components/Alert'
import { useUpdateUser } from 'services/user'

export type BlockAction = 'block' | 'unblock'

type BlockUserActionProps = {
  user: User
}

export const BlockUserAction: FC<BlockUserActionProps> = ({ user }) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState('idle')
  const [updateUser] = useUpdateUser(user.id)

  const onChange = (blocked: boolean) => {
    setStatus('loading')
    updateUser(
      { blocked },
      {
        onError: () => setStatus('error'),
        onSuccess: () => setStatus('success'),
        onSettled: () => {
          setTimeout(() => setStatus('idle'), 1000)
        },
      },
    )
  }

  return (
    <Card>
      <Stack space="medium">
        <Flex justify="between" align="center">
          <Stack space="xsmall">
            <Heading size="widget">
              {user.blocked ? t('user.edit.card.unblock.title') : t('user.edit.card.block.title')}
            </Heading>
            <Text>
              {user.blocked ? t('user.edit.card.unblock.desc') : t('user.edit.card.block.desc')}
            </Text>
          </Stack>
          <Button
            variant="secondary"
            loading={status === 'loading'}
            onClick={() => onChange(!user.blocked)}
          >
            {user.blocked ? t('user.edit.card.unblock.cta') : t('user.edit.card.block.cta')}
          </Button>
        </Flex>

        {status === 'success' && (
          <Alert tone="positive" full>
            {t('common.messages.success')}
          </Alert>
        )}

        {status === 'error' && (
          <Alert tone="critical" full>
            {t('common.messages.error')}
          </Alert>
        )}
      </Stack>
    </Card>
  )
}

BlockUserAction.displayName = 'BlockUserAction'
